/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './HODDashboard.css';

// --- SVG Icons ---
const Icons = {
    Print: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>,
    Download: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
    Refresh: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>,
    Student: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    Book: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>,
    Check: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>,
    Alert: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>,
    Info: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

export default function HODDashboard() {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTerm, setCurrentTerm] = useState(1);
    const [activityFromDate, setActivityFromDate] = useState('');
    const [activityToDate, setActivityToDate] = useState('');
    const [selectedSubjectForGrade, setSelectedSubjectForGrade] = useState('');

    // Subject hooks must be declared before any early returns
    const subjectList = Array.from(new Set(((analyticsData && analyticsData.pass_rates_by_subject) || []).map(s => (s.subject_name || '').trim()))).filter(Boolean).sort();

    useEffect(() => {
        if (!selectedSubjectForGrade && subjectList.length) {
            setSelectedSubjectForGrade(subjectList[0]);
        }
    }, [subjectList, selectedSubjectForGrade]);

    const gradePerformanceData = useMemo(() => {
        if (!selectedSubjectForGrade) return [];
        const pass_rates = (analyticsData && analyticsData.pass_rates_by_subject) || [];
        const filtered = pass_rates.filter(s => (s.subject_name || '').trim() === selectedSubjectForGrade);
        const grouped = filtered.reduce((acc, row) => {
            const g = String(row.grade_level || '');
            const avg = Number(row.average_percentage) || Number(row.average || 0) || 0;
            const pass = Number(row.passed_count || row.passed_students || row.pass_rate || 0) || 0;
            const total = Number(row.total_students) || Number(row.student_count) || 0;
            if (!acc[g]) acc[g] = { grade_level: g, total_students: 0, total_avg: 0, passed: 0 };
            acc[g].total_students += total;
            acc[g].total_avg += avg * (total || 1);
            acc[g].passed += pass;
            return acc;
        }, {});

        const out = Object.keys(grouped).map(k => {
            const item = grouped[k];
            const avgPct = item.total_students > 0 ? Number((item.total_avg / item.total_students).toFixed(1)) : 0;
            const passRate = item.total_students > 0 ? Number(((item.passed / item.total_students) * 100).toFixed(1)) : 0;
            return { grade_level: k, average_percentage: avgPct, pass_rate: passRate };
        }).sort((a, b) => Number(a.grade_level) - Number(b.grade_level));

        return out;
    }, [selectedSubjectForGrade, analyticsData]);

    useEffect(() => {
        fetchAnalytics();
    }, [currentTerm]);

    // Hide global sidebar while this dashboard is mounted
    useEffect(() => {
        try {
            document.body.classList.add('hide-sidebar');
        } catch (e) {}
        return () => {
            try {
                document.body.classList.remove('hide-sidebar');
            } catch (e) {}
        };
    }, []);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const authToken = localStorage.getItem('auth_token');
            const schoolId = localStorage.getItem('school_id');
            const url = `${process.env.REACT_APP_API_URL}/fetch_hod_analytics.php?school_id=${schoolId}` + (currentTerm ? `&term=${currentTerm}` : '');
            const response = await axios.get(url, { headers: { 'Authorization': `Bearer ${authToken}` } });

            console.log('Analytics Response:', response.data);

            if (response.data.success) {
                setAnalyticsData(response.data);
                setError(null);
            } else {
                setError(response.data.error || 'Failed to load analytics data');
            }
        } catch (err) {
            console.error('Analytics Fetch Error:', err.response?.data || err.message);
            setError(err.response?.data?.error || 'Failed to fetch analytics.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="hod-dashboard loading-state">
                <div className="spinner"></div>
                <p>Loading Analytics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="hod-dashboard error-state">
                <p>{error}</p>
                <button onClick={fetchAnalytics} className="btn-retry">Retry</button>
            </div>
        );
    }

    const {
        overall_stats,
        pass_rates_by_subject,
        performance_by_grade,
        top_performers,
        bottom_performers,
        bottom_performers_by_grade,
        students_needing_support_by_subject,
        insights
    } = analyticsData;

    // Group subjects (coerce numeric fields and use API field names)

    const uniqueSubjectCount = new Set(
        (pass_rates_by_subject || [])
            .map(s => String(s.subject_name || '').trim().toLowerCase())
            .filter(Boolean)
    ).size;

    // Compute subject distribution as unique subjects per grade (avoid counting classes)
    const subjectDistributionData = (() => {
        const rows = (analyticsData && analyticsData.pass_rates_by_subject) || [];
        const seen = new Set();
        const counts = {};
        rows.forEach(r => {
            const grade = String(r.grade_level || '').trim();
            const subj = String(r.subject_name || '').trim();
            if (!grade || !subj) return;
            const key = `${grade}::${subj}`.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            counts[grade] = (counts[grade] || 0) + 1;
        });
        return Object.keys(counts).map(g => ({ grade_level: g, name: `Grade ${g}`, value: counts[g] }));
    })();

    // Get personalized greeting based on time of day
    const getGreeting = () => {
        const hour = new Date().getHours();
        const username = localStorage.getItem('username') || 'User';
        let greeting = '';

        if (hour < 12) {
            greeting = 'Good morning';
        } else if (hour < 17) {
            greeting = 'Good afternoon';
        } else {
            greeting = 'Good evening';
        }

        return `${greeting}, ${username}!`;
    };

    // Helpers for export: grading helpers
    const calculateLevel = (percentage) => {
        const rounded = Math.round(percentage);
        if (rounded >= 80) return 7;
        if (rounded >= 70) return 6;
        if (rounded >= 60) return 5;
        if (rounded >= 50) return 4;
        if (rounded >= 40) return 3;
        if (rounded >= 30) return 2;
        return 1;
    };

    // Export teacher-style statistics sheets for one or more terms
    const exportToExcelForTerms = async (terms) => {
        if (!analyticsData) return;

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Marksure HOD';
        workbook.created = new Date();

        const thinBorder = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
        };

        const fillGrey = (argb) => ({
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb }
        });

        const styleRange = (ws, fromRow, toRow, fromCol, toCol, style) => {
            for (let r = fromRow; r <= toRow; r++) {
                for (let c = fromCol; c <= toCol; c++) {
                    const cell = ws.getCell(r, c);
                    if (style.font) cell.font = style.font;
                    if (style.fill) cell.fill = style.fill;
                    if (style.border) cell.border = style.border;
                    if (style.alignment) cell.alignment = style.alignment;
                }
            }
        };

        const subjects = analyticsData.pass_rates_by_subject || [];
        const usedSheetNames = new Set();
        const invalidSheetCharRegex = new RegExp('[\\\\/?*\\[\\]:]', 'g');

        const getUniqueSheetName = (baseName) => {
            const sanitized = String(baseName || 'Sheet').replace(invalidSheetCharRegex, ' ').trim() || 'Sheet';
            const base = sanitized.substring(0, 31);
            if (!usedSheetNames.has(base)) {
                usedSheetNames.add(base);
                return base;
            }

            let i = 2;
            while (i < 1000) {
                const suffix = ` (${i})`;
                const candidate = `${base.substring(0, 31 - suffix.length)}${suffix}`;
                if (!usedSheetNames.has(candidate)) {
                    usedSheetNames.add(candidate);
                    return candidate;
                }
                i++;
            }

            const fallback = `${Date.now()}`.substring(0, 31);
            usedSheetNames.add(fallback);
            return fallback;
        };

        // Preload student name map for lookups
        let allStudentsMap = {};
        try {
            const allRes = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_all_students.php`);
            (allRes.data.students || []).forEach(s => { allStudentsMap[s.student_id] = s; });
        } catch (e) {
            allStudentsMap = {};
        }

        for (const term of terms) {
            for (const subj of subjects) {
                try {
                    const id = subj.subject_id;
                    const [classRes, secRes, marksRes] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${id}`),
                        axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${term}`),
                        axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=${term}`)
                    ]);

                    const sections = secRes.data.sections || [];
                    const marks = marksRes.data.marks || [];
                    let students = (classRes.data && classRes.data.students) ? classRes.data.students.map(s => ({ ...s })) : [];

                    if (students.length === 0) {
                        const sids = Array.from(new Set(marks.map(m => String(m.student_id))));
                        students = sids.map(sid => {
                            const s = allStudentsMap[sid] || { student_id: sid, first_name: '', last_name: '' };
                            return { student_id: sid, first_name: s.first_name || '', last_name: s.last_name || '' };
                        });
                    }

                    const className = classRes?.data?.class_name || '';
                    const grade = classRes?.data?.grade || subj.grade_level || '';
                    const sheetName = getUniqueSheetName(`T${term} ${subj.subject_name} Gr${grade} ${className}`);
                    const ws = workbook.addWorksheet(sheetName);

                    const nameLower = (subj.subject_name || '').toLowerCase();
                    const isHomeLanguage = nameLower.includes('home language') || nameLower.includes('huistaal');
                    const passMark = isHomeLanguage ? 50 : 40;

                    ws.addRow(['STATISTIC AND DIAGNOSTIC ANALYSIS OF MARKS']);
                    ws.addRow([]);
                    ws.addRow(['SUBJECT:', subj.subject_name, '', '', 'MARK:', sections.reduce((sum, s) => sum + (parseFloat(s.max_mark) || 0), 0)]);
                    ws.addRow(['EXAMINER:', '', '', '', 'GRADE:', grade]);
                    ws.addRow(['Term', term]);
                    ws.addRow([]);

                    ws.addRow([
                    'Section', 'Total in class', 'Total written', 'Class average',
                    'Highest mark', 'Lowest mark', 'Total failed', '% Failures',
                    'Total that passed', '% Passed',
                    '0 - 29', '30 - 39', '40 - 49', '50 - 59', '60 - 69', '70 - 79', '80 - 100',
                    'Total (1-7 = Total/Total written)'
                    ]);

                    const sectionRows = [];

                    sections.forEach(section => {
                        const max = parseFloat(section.max_mark) || 100;
                        const sectionMarks = marks
                        .filter(m => Number(m.section_id) === Number(section.id) && m.mark !== '' && m.mark != null)
                        .map(m => parseFloat(m.mark))
                        .filter(v => !isNaN(v));

                        const totalWritten = sectionMarks.length;
                        const levelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
                        let passed = 0;
                        let failed = 0;
                        let percentSum = 0;

                        sectionMarks.forEach(mark => {
                            const pct = max > 0 ? (mark / max) * 100 : 0;
                            percentSum += pct;

                            const lvl = calculateLevel(pct);
                            levelDist[lvl] += 1;

                            if (Math.round(pct) >= passMark) {
                                passed += 1;
                            } else {
                                failed += 1;
                            }
                        });

                        const total17 = Object.values(levelDist).reduce((a, b) => a + b, 0);
                        sectionRows.push([
                        `${section.section_name} (${max})`,
                        students.length,
                        totalWritten,
                        totalWritten > 0 ? `${(percentSum / totalWritten).toFixed(1)}%` : '',
                        totalWritten > 0 ? Math.max(...sectionMarks) : '',
                        totalWritten > 0 ? Math.min(...sectionMarks) : '',
                        failed,
                        totalWritten > 0 ? `${Math.round((failed / totalWritten) * 100)}%` : '0%',
                        passed,
                        totalWritten > 0 ? `${Math.round((passed / totalWritten) * 100)}%` : '0%',
                        levelDist[1], levelDist[2], levelDist[3], levelDist[4], levelDist[5], levelDist[6], levelDist[7],
                        totalWritten > 0 ? `${total17}/${totalWritten}` : ''
                        ]);
                    });

                    sectionRows.forEach(r => ws.addRow(r));

                    // Build OVERALL totals from unique students (not sum of section entries)
                    const sectionMaxById = {};
                    sections.forEach((s) => {
                        sectionMaxById[Number(s.id)] = parseFloat(s.max_mark) || 0;
                    });

                    const studentTotals = {};
                    marks.forEach((m) => {
                        const sid = String(m.student_id || '');
                        const secId = Number(m.section_id);
                        const markNum = parseFloat(m.mark);
                        const max = sectionMaxById[secId] || 0;
                        if (!sid || Number.isNaN(markNum) || max <= 0) return;

                        if (!studentTotals[sid]) {
                            studentTotals[sid] = { obtained: 0, max: 0 };
                        }
                        studentTotals[sid].obtained += markNum;
                        studentTotals[sid].max += max;
                    });

                    const overallPercents = [];
                    const overallLevelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
                    let overallPassed = 0;
                    let overallFailed = 0;

                    Object.values(studentTotals).forEach((t) => {
                        if (t.max <= 0) return;
                        const pct = (t.obtained / t.max) * 100;
                        overallPercents.push(pct);

                        const lvl = calculateLevel(pct);
                        overallLevelDist[lvl] += 1;

                        if (Math.round(pct) >= passMark) overallPassed += 1;
                        else overallFailed += 1;
                    });

                    const totalWrittenOverall = overallPercents.length;
                    const total17Overall = Object.values(overallLevelDist).reduce((a, b) => a + b, 0);
                    ws.addRow([
                    'OVERALL TOTAL',
                    students.length,
                    totalWrittenOverall,
                    totalWrittenOverall > 0 ? `${(overallPercents.reduce((a, b) => a + b, 0) / totalWrittenOverall).toFixed(1)}%` : '',
                    overallPercents.length > 0 ? `${Math.max(...overallPercents).toFixed(1)}%` : '',
                    overallPercents.length > 0 ? `${Math.min(...overallPercents).toFixed(1)}%` : '',
                    overallFailed,
                    totalWrittenOverall > 0 ? `${Math.round((overallFailed / totalWrittenOverall) * 100)}%` : '0%',
                    overallPassed,
                    totalWrittenOverall > 0 ? `${Math.round((overallPassed / totalWrittenOverall) * 100)}%` : '0%',
                    overallLevelDist[1], overallLevelDist[2], overallLevelDist[3], overallLevelDist[4], overallLevelDist[5], overallLevelDist[6], overallLevelDist[7],
                    totalWrittenOverall > 0 ? `${total17Overall}/${totalWrittenOverall}` : ''
                    ]);

                    ws.columns = [
                    { width: 24 }, { width: 12 }, { width: 12 }, { width: 13 }, { width: 12 }, { width: 12 },
                    { width: 11 }, { width: 11 }, { width: 13 }, { width: 10 },
                    { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 },
                    { width: 18 }
                    ];

                    // Teacher-style formatting for readability
                    ws.mergeCells(1, 1, 1, 18);
                    ws.getRow(1).height = 24;
                    ws.getRow(8).height = 22;

                    styleRange(ws, 1, 1, 1, 18, {
                        font: { bold: true, size: 12 },
                        fill: fillGrey('FFD9D9D9'),
                        border: thinBorder,
                        alignment: { horizontal: 'center', vertical: 'middle' }
                    });

                    styleRange(ws, 3, 5, 1, 1, {
                        font: { bold: true },
                        fill: fillGrey('FFEFEFEF'),
                        border: thinBorder,
                        alignment: { horizontal: 'left', vertical: 'middle' }
                    });

                    styleRange(ws, 3, 5, 2, 18, {
                        border: thinBorder,
                        alignment: { horizontal: 'left', vertical: 'middle' }
                    });

                    styleRange(ws, 8, 8, 1, 18, {
                        font: { bold: true },
                        fill: fillGrey('FFE5E5E5'),
                        border: thinBorder,
                        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true }
                    });

                    const lastDataRow = ws.rowCount;
                    if (lastDataRow >= 9) {
                        styleRange(ws, 9, lastDataRow, 1, 18, {
                            border: thinBorder,
                            alignment: { horizontal: 'center', vertical: 'middle' }
                        });

                        styleRange(ws, 9, lastDataRow, 1, 1, {
                            border: thinBorder,
                            alignment: { horizontal: 'left', vertical: 'middle' }
                        });

                        styleRange(ws, lastDataRow, lastDataRow, 1, 18, {
                            font: { bold: true },
                            fill: fillGrey('FFF5F5F5'),
                            border: thinBorder,
                            alignment: { horizontal: 'center', vertical: 'middle' }
                        });

                        styleRange(ws, lastDataRow, lastDataRow, 1, 1, {
                            font: { bold: true },
                            fill: fillGrey('FFF5F5F5'),
                            border: thinBorder,
                            alignment: { horizontal: 'left', vertical: 'middle' }
                        });
                    }

                } catch (err) {
                    console.error('Export subject error', subj.subject_name, err);
                }
            }
        }

        try {
            const buf = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const termLabel = terms.length === 1 ? `Term_${terms[0]}` : 'All_Terms';
            a.download = `HOD_Subjects_Detailed_${termLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert('Failed to generate Excel: ' + e.message);
        }
    };

    const exportToExcel = async () => exportToExcelForTerms([currentTerm]);
    const exportAllTermsToExcel = async () => exportToExcelForTerms([1, 2, 3, 4]);

    const exportMarkChangesToExcel = async () => {
        try {
            if (activityFromDate && activityToDate && activityFromDate > activityToDate) {
                alert('From date must be before or equal to To date.');
                return;
            }

            const params = new URLSearchParams();
            if (activityFromDate) params.set('start_date', activityFromDate);
            if (activityToDate) params.set('end_date', activityToDate);
            // Avoid stale cached responses in some browsers/proxies
            params.set('_ts', String(Date.now()));

            const authToken = localStorage.getItem('auth_token');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/export_mark_changes.php?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            const activities = res?.data?.activities || [];

            // Fail-safe: apply date filter again on client to guarantee export matches selected range
            const filteredActivities = activities.filter((a) => {
                const rowDate = String(a?.updated_at || '').slice(0, 10);
                if (!rowDate) return false;
                if (activityFromDate && rowDate < activityFromDate) return false;
                if (activityToDate && rowDate > activityToDate) return false;
                return true;
            });

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Marksure HOD';
            workbook.created = new Date();
            const ws = workbook.addWorksheet('Mark Changes');

            ws.addRow(['Mark Changes Report']);
            ws.addRow(['Generated', new Date().toISOString()]);
            ws.addRow(['From', activityFromDate || 'All']);
            ws.addRow(['To', activityToDate || 'All']);
            ws.addRow([]);
            ws.addRow(['Student', 'Subject', 'Grade', 'Section', 'Action', 'Old Value', 'New Value', 'Max Mark', 'Percent', 'Changed By', 'Role', 'Date']);

            filteredActivities.forEach(a => {
                ws.addRow([
                    a.student_name || '',
                    a.subject_name || '',
                    a.grade_level ?? '',
                    a.section_name || '',
                    a.action_type || '',
                    a.old_value ?? '',
                    a.new_value ?? '',
                    a.max_mark ?? '',
                    a.average ?? '',
                    a.changed_by || '',
                    a.user_role || '',
                    a.updated_at || ''
                ]);
            });

            ws.columns = [
                { width: 24 }, { width: 24 }, { width: 8 }, { width: 18 }, { width: 12 }, { width: 10 },
                { width: 10 }, { width: 10 }, { width: 10 }, { width: 16 }, { width: 10 }, { width: 22 }
            ];

            const buf = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const rangeTag = activityFromDate || activityToDate
                ? `_${activityFromDate || 'start'}_to_${activityToDate || 'now'}`
                : '';
            a.download = `HOD_Mark_Changes${rangeTag}_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert('Failed to export mark changes: ' + (e?.message || 'Unknown error'));
        }
    };

    return (
        <div className="hod-dashboard">
            <div className="dashboard-header">
                <div>
                    <h1>{getGreeting()}</h1>
                    <p className="subtitle">School Performance Overview</p>
                </div>
                <div className="header-actions no-print">
                    <div style={{ display: 'inline-flex', gap: '6px', marginRight: '8px' }}>
                        {[1, 2, 3, 4].map(term => (
                            <button
                                key={term}
                                onClick={() => setCurrentTerm(term)}
                                className="action-btn"
                                style={{ padding: '6px 10px', background: currentTerm === term ? '#2e7d32' : undefined, color: currentTerm === term ? '#fff' : undefined }}
                            >
                                T{term}
                            </button>
                        ))}
                    </div>
                  
                    
                    
                    <button onClick={exportToExcel} className="action-btn"><Icons.Download /> Excel T{currentTerm}</button>
                    <button onClick={exportAllTermsToExcel} className="action-btn"><Icons.Download /> Excel T1-4</button>
                    <button onClick={exportMarkChangesToExcel} className="action-btn"><Icons.Download /> Mark Changes</button>
                      <input
                        type="date"
                        value={activityFromDate}
                        onChange={(e) => setActivityFromDate(e.target.value)}
                        className="modern-input"
                        style={{ width: '150px' }}
                        title="Mark changes from date"
                    />
                    <input
                        type="date"
                        value={activityToDate}
                        onChange={(e) => setActivityToDate(e.target.value)}
                        className="modern-input"
                        style={{ width: '150px' }}
                        title="Mark changes to date"
                    />
                    <button onClick={fetchAnalytics} className="action-btn"><Icons.Refresh /> Refresh</button>
                </div>
            </div>

            {/* Insights */}
            {insights && insights.length > 0 && (
                <div className="insights-grid">
                    {insights.map((insight, index) => (
                        <div key={index} className={`insight-card ${insight.type}`}>
                            <div className="insight-icon">
                                {insight.type === 'warning' ? <Icons.Alert /> : <Icons.Info />}
                            </div>
                            <div>
                                <h3>{insight.title}</h3>
                                <p>{insight.message}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Key Metrics */}
            <div className="stats-cards">
                <div className="stat-card">
                    <div className="stat-icon-wrapper blue"><Icons.Student /></div>
                    <div className="stat-content">
                        <h3>{overall_stats.total_students}</h3>
                        <p>Total Students</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper green"><Icons.Book /></div>
                    <div className="stat-content">
                        <h3>{uniqueSubjectCount}</h3>
                        <p>Active Subjects</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper orange"><Icons.Check /></div>
                    <div className="stat-content">
                        <h3>{overall_stats.total_marks}</h3>
                        <p>Assessments Logged</p>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-section">

                <div className="chart-card">
                    <h3>Performance by Grade — Subject</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 14, color: '#444' }}>
                            Select Subject:
                        </div>
                        <select value={selectedSubjectForGrade} onChange={(e) => setSelectedSubjectForGrade(e.target.value)} style={{ minWidth: 220 }}>
                            {subjectList.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={gradePerformanceData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="grade_level" tickFormatter={v => `Gr ${v}`} />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="average_percentage" fill="#4CAF50" name="Average (%)" />
                            <Bar dataKey="pass_rate" fill="#FF9800" name="Pass Rate (%)" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card">
                    <h3>Performance by Grade</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={performance_by_grade}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="grade_level" tickFormatter={v => `Gr ${v}`} />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="average_percentage" stroke="#4CAF50" strokeWidth={3} name="Average (%)" />
                            <Line type="monotone" dataKey="pass_rate" stroke="#FF9800" strokeWidth={3} name="Pass Rate (%)" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card">
                    <h3>Subjects Distribution</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={subjectDistributionData}
                                cx="50%" cy="50%"
                                innerRadius={60} outerRadius={100}
                                dataKey="value"
                                nameKey="name"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {subjectDistributionData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value} subjects`, 'Count']} />
                            <Legend verticalAlign="bottom" formatter={(value) => value} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Tables Section */}
            <div className="tables-grid">

                {/* Top Performers */}
                <div className="table-card">
                    <div className="card-header">
                        <h3>Top Performers</h3>
                    </div>
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th>Rank</th>
                                <th>Student</th>
                                <th>Class</th>
                                <th>Grade</th>
                                <th>Avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top_performers.map((s, i) => (
                                <tr key={s.student_id}>
                                    <td><span className="rank-badge">#{i + 1}</span></td>
                                    <td>{s.name}</td>
                                    <td>{s.class_name || ''}</td>
                                    <td>{s.grade_level}</td>
                                    <td className="highlight-text">{s.average_percentage}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Needs Support */}
                <div className="table-card">
                    <div className="card-header">
                        <h3>⚠️ Students Needing Support</h3>
                    </div>
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th>Priority</th>
                                <th>Student</th>
                                <th>Class</th>
                                <th>Grade</th>
                                <th>Avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bottom_performers.map((s, i) => (
                                <tr key={s.student_id}>
                                    <td>
                                        <span className={`priority-badge ${i < 3 ? 'high' : 'med'}`}>
                                            {i < 3 ? 'High' : 'Med'}
                                        </span>
                                    </td>
                                    <td>{s.name}</td>
                                    <td>{s.class_name || ''}</td>
                                    <td>{s.grade_level}</td>
                                    <td className="danger-text">{s.average_percentage}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Intervention By Subject */}
            {students_needing_support_by_subject && students_needing_support_by_subject.length > 0 && (
                <div className="detailed-section">
                    <h3>Intervention Required by Subject</h3>
                    <div className="support-grid">
                        {students_needing_support_by_subject.map((subject) => {
                            const visibleStudents = (subject.students || []).slice(0, 8);
                            const remaining = Math.max((subject.students || []).length - visibleStudents.length, 0);
                            return (
                                <div key={subject.subject_id} className="support-card">
                                    <div className="support-header">
                                        <h4>{subject.subject_name} (Gr {subject.grade_level})</h4>
                                        <span className="class-badge">{subject.class_name}</span>
                                        <span className="count-badge">{subject.total_students} Students</span>
                                    </div>
                                    <div className="student-list">
                                        {visibleStudents.map(s => (
                                            <div key={`${subject.subject_id}-${s.student_id}`} className="student-row">
                                                    <span>{s.name} {s.grade_level ? (<span className="class-inline">• Grade {s.grade_level}</span>) : null}</span>
                                                    <span className="score">{s.average_percentage}%</span>
                                            </div>
                                        ))}
                                        {remaining > 0 && (
                                            <div className="student-row">
                                                <span>+{remaining} more...</span>
                                                <span className="score"> </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Support By Grade */}
            {bottom_performers_by_grade && (
                <div className="detailed-section">
                    <h3>Intervention Required by Grade</h3>
                    <div className="support-grid">
                        {bottom_performers_by_grade.map((grade) => (
                            <div key={grade.grade_level} className="support-card">
                                <div className="support-header">
                                    <h4>Grade {grade.grade_level}</h4>
                                    <span className="count-badge">{grade.students.length} Students</span>
                                </div>
                                <div className="student-list">
                                    {grade.students.map(s => (
                                        <div key={s.student_id} className="student-row">
                                            <span>{s.name} {s.class_name ? (<span className="class-inline">• {s.class_name}</span>) : null}</span>
                                            <span className="score">{s.average_percentage}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}